import { Trans, useLingui } from '@lingui/react/macro';
import { min } from 'date-fns';

import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Title from '@/design_system/Typography/Title';
import { useInvitations } from '@/models/userRole';
import { InviteUser } from '@/routes/SettingsBeta/SettingsOrganization/sections/Members/subsections/Invitations/components/InviteUser';
import { RemoveUser } from '@/routes/SettingsBeta/SettingsOrganization/sections/Members/subsections/Invitations/components/RemoveUser';
import { ResendInvitation } from '@/routes/SettingsBeta/SettingsOrganization/sections/Members/subsections/Invitations/components/ResendInvitation';
import { UpdateUserRoles } from '@/routes/SettingsBeta/SettingsOrganization/sections/Members/subsections/Invitations/components/UpdateUserRoles';
import { formatDate } from '@/utils/date';

export const Invitations = () => {
  const { t } = useLingui();
  const { data: { invitations } = {} } = useInvitations({ limit: 500 });

  if (!invitations?.length) {
    return null;
  }

  return (
    <Box padding="24px" gap="24px">
      <Stack row alignItems="center" justifyContent="space-between">
        <Title size="200" as="h2">
          <Trans id="settings.users.invited.title">Invited members</Trans>
        </Title>
        <InviteUser />
      </Stack>
      <Table
        ariaLabel={t({ id: 'settings.users.invited.table.label', message: 'Invited members' })}
        columnWidths="minmax(200px, 1fr) minmax(110px, 1fr) minmax(110px, 1fr) auto"
      >
        <Header>
          <Row>
            <Column>
              <Trans id="settings.users.invited.columns.email">Email</Trans>
            </Column>
            <Column>
              <Trans id="settings.users.invited.columns.roles">Roles</Trans>
            </Column>
            <Column>
              <Trans id="settings.users.invited.columns.invitation-date">Invitation date</Trans>
            </Column>
            <Column
              ariaLabel={t({
                id: 'settings.users.column.actions',
                message: 'Actions',
              })}
            />
          </Row>
        </Header>
        <Body>
          {invitations?.map((invitation) => (
            <Row key={invitation.email}>
              <Cell className="sentry-mask">
                <b>{invitation.email}</b>
              </Cell>
              <Cell>
                <Stack gap="0.25rem">
                  {invitation.userRoles
                    .map((userRole) => {
                      if (userRole.entity) {
                        return `${userRole.role.name} (${userRole.entity.name})`;
                      }

                      return userRole.role.name;
                    })
                    .map((label) => (
                      <div key={label}>{label}</div>
                    ))}
                </Stack>
              </Cell>
              <Cell>
                {formatDate(
                  min(invitation.userRoles.map((userRole) => new Date(userRole.invitedAt))),
                  {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }
                )}
              </Cell>
              <Cell>
                <Stack row gap="4px">
                  <ResendInvitation email={invitation.email} />
                  <UpdateUserRoles
                    user={{
                      email: invitation.email,
                      roles: invitation.userRoles,
                    }}
                  />
                  <RemoveUser
                    user={{
                      email: invitation.email,
                      roles: invitation.userRoles,
                    }}
                  />
                </Stack>
              </Cell>
            </Row>
          ))}
        </Body>
      </Table>
    </Box>
  );
};
